import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import React, { startTransition, useCallback, useEffect } from "react"
import { ErrorsType, FormPropsType } from "./types"
import { toastMsg } from "../../../utils/notifications"

import {
  FormFieldsType,
  FormItemType,
  getInputEventValue,
  getInputHumanValue,
  json_request,
} from "../../../utils/forms"
import * as styles from "./form.module.scss"
import classNames from "classnames"

const Icon: any = React.lazy(() => import("../icon"))
const Element: any = React.lazy(() => import("../"))
const Spinner: any = React.lazy(() => import("../../spinner"))
const Markdown: any = React.lazy(() => import("../../markdown"))

const INPUT_FIELDS = new Set([
  "input",
  "select",
  "textarea",
  "checkbox",
  "button",
  "recaptcha2",
  "google-places",
  "country-prefix",
])

export default function Form({
  className,
  form,
  setForm,
  errors,
  setErrors,
  beforeSubmit,
  formatParams,
  validate,
  validateResponse,
  onSuccess,
  onFocus,
  onBlur,
  onClick,
  onChange,
  onError,
  afterSubmit,
  loading,
}: FormPropsType) {
  const { executeRecaptcha } = useGoogleReCaptcha()

  const showErrors = (errors: ErrorsType, showInputName: boolean = true) => (
    <div>
      <ul className={styles.inputError}>
        {Object.entries(errors).map(([input, error], k: number) => (
          <li key={`error-form${k}`}>
            <Icon icon={{ icon: "times", group: "FAS" }} />
            <span>
              {showInputName ? <strong>{input}:</strong> : ""} {error}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    let errors: any = {}
    let params = Object.values(form.fields).reduce((acc, field) => {
      if (INPUT_FIELDS.has(field.type)) {
        // @ts-ignore
        if (field.required && !field.value) {
          //@ts-ignore
          errors[field.input_name] = `${
            //@ts-ignore
            field.label ? field.label : field.placeholder
          } is required`
          return acc
        }
        // @ts-ignore
        // Skip non-checked radio buttons
        if (field.input_type === "radio" && !field.checked) {
          return acc
        }
        // @ts-ignore
        acc[field.input_name] = getInputHumanValue(field.type, field.value)
      }
      return acc
    }, {} as any)

    // Get reCAPTCHA v3 token
    if (form.settings.recaptcha_v3) {
      params.recaptcha_token = await verifyRecaptcha()
      if (!params.recaptcha_token) {
        errors.recaptcha = "reCAPTCHA failed!"
      }
    }

    // Check recaptcha v2 if it's within the fields
    if (form.fields.recaptcha && !params.recaptcha) {
      errors.recaptcha = "Recaptcha is mandatory"
    }

    if (validate) {
      errors = { ...errors, ...validate(form.fields, params) }
    }
    if (Object.keys(errors).length) {
      startTransition(() => {
        setErrors(errors)
        toastMsg(showErrors(errors, false))
      })
      return
    }

    if (beforeSubmit) {
      beforeSubmit(form)
    }

    if (formatParams) {
      params = formatParams(params)
    }

    // If is not an AJAX request, the page will be redirected into the form submit URL
    if (!form.settings.form_ajax_request) {
      // @ts-ignore
      return e.target.submit()
    }
    const response = await json_request(
      form.settings.form_submit,
      params,
      form.settings.form_method,
      form.settings.form_recipient
        ? {
            "recipient-id": `${form.settings.form_recipient}`,
          }
        : {}
    )
    startTransition(() => {
      // ON SUCCESS:
      if (
        response.status === 200 &&
        (!validateResponse || validateResponse(response))
      ) {
        if (onSuccess) {
          // Execute onSuccess action when defined:
          onSuccess(response, form.settings.form_success_msg)
        } else {
          // Otherwise just show success message:
          toastMsg(
            form.settings.form_success_msg ? (
              <Markdown>{form.settings.form_success_msg}</Markdown>
            ) : (
              "Success! Your form has been successfully sent. Thank you for reaching out, we will get back to you as soon as possible."
            ),
            "success"
          )
        }

        if (form.settings.form_redirection) {
          setTimeout(() => {
            window.location.href = form.settings.form_redirection as string
          }, 3000)
        }

        // Reset all form values:
        setForm({
          ...form,
          fields: Object.entries(form.fields).reduce((acc, [name, field]) => {
            acc[name] = INPUT_FIELDS.has(field.type) // @ts-ignore
              ? { ...field, value: "" }
              : field
            return acc
          }, {} as FormFieldsType),
        })
        // ON ERROR:
      } else if (onError) {
        onError(response)
        // ELSE ON ERROR:
      } else if (response.errors) {
        let newErrors: ErrorsType = Object.entries(response.errors).reduce(
          (acc, [inputName, errorList]) => {
            acc[inputName] = errorList.join(", ")
            return acc
          },
          {} as ErrorsType
        )
        toastMsg(showErrors(newErrors))
        setErrors(newErrors)
        // ELSE ON ERROR:
      } else if (response.error) {
        toastMsg(
          showErrors(
            {
              failed: response.error ? (
                <Markdown>{response.error}</Markdown>
              ) : (
                "Oops. Something went wrong. Please try again later, or contact us at members@tfehotels.com"
              ),
            },
            false
          )
        )
      }
    })

    if (afterSubmit) {
      afterSubmit(response)
    }
  }

  const verifyRecaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available")
      return
    }
    return await executeRecaptcha(form.settings.recaptcha_action)
  }, [executeRecaptcha])

  useEffect(() => {
    if (form.settings.recaptcha_v3) {
      verifyRecaptcha()
    }
  }, [form.settings.recaptcha_v3, verifyRecaptcha])

  const buildFormElement = (item: FormItemType, k: number) => {
    if (typeof item === "string") {
      const field: any = form.fields[item]
      return (
        <div
          className={`${field.type} ${field.type}-${field.name}`}
          key={`ff-${form.settings.pk}-${item}`}
        >
          {INPUT_FIELDS.has(field.type) ? (
            <Element
              className={classNames({
                [field.data.class]: Boolean(field.data.class),
                error: Boolean(errors[item]),
              })}
              onFocus={(e: React.FocusEvent) => {
                if (onFocus) onFocus(e)
              }}
              onBlur={(e: React.FocusEvent) => {
                if (onBlur) onBlur(e)
              }}
              onClick={(e: React.MouseEvent) => {
                // Only for checkboxes/Radios
                if (["checkbox", "radio"].includes(field.input_type)) {
                  let newForm = { ...form }
                  if (field.input_type === "checkbox") {
                    newForm.fields = {
                      ...form.fields,
                      [item]: { ...field, value: !field.value },
                    }
                  } else {
                    // Radio buttons (sets the "checked" flag on all radio buttons with same name)
                    newForm.fields = Object.entries(form.fields).reduce(
                      (acc, [fieldName, attrs]) => {
                        acc[fieldName] = attrs //@ts-ignore
                        if (attrs.input_name === field.input_name) {
                          //@ts-ignore
                          acc[fieldName].checked = //@ts-ignore
                            field.value === acc[fieldName].value
                        }
                        return acc
                      },
                      {} as FormFieldsType
                    )
                  }
                  startTransition(() => {
                    setForm(newForm)
                    if (errors[item]) {
                      setErrors({}) // reset errors
                    }
                  })
                }
                if (onClick) onClick(e)
              }}
              checked={
                field.input_type === "radio" ? !!field.checked : undefined
              }
              onChange={(e: any) => {
                if (!["checkbox", "radio"].includes(field.input_type)) {
                  startTransition(() => {
                    setForm({
                      ...form,
                      fields: {
                        ...form.fields,
                        [item]: {
                          ...field,
                          value: getInputEventValue(field.type, e),
                          data: e.target?.data
                            ? { ...field.data, ...e.target.data }
                            : field.data,
                        },
                      },
                    })

                    if (errors[item]) {
                      setErrors({}) // reset errors
                    }
                  })
                }
                if (onChange) onChange(e)
              }}
              {...{
                ...field,
                value: field.value ? field.value : "",
              }}
            />
          ) : (
            // Display Titles, Texts, Links, etc..
            <Element {...field} />
          )}
        </div>
      )
    }
    // Otherwise is a section
    return (
      <div
        className={`form-section ${item.name}`}
        key={`ff-${form.settings.pk}-${item.name}`}
      >
        {item.children.map(buildFormElement)}
      </div>
    )
  }
  return (
    <form
      className={className}
      action={form.settings.form_submit}
      method={form.settings.form_method}
      onSubmit={e => handleSubmit(e)}
      noValidate
    >
      {loading ? <Spinner color="#00b5ee" /> : form.items.map(buildFormElement)}
    </form>
  )
}
